<template>
    <!-- Read from the API and never invented here: it is the number somebody quotes when
         they ring us, so a portal that has lost the API shows nothing rather than a
         version of the wrong half of the system. -->
    <button
        v-if="version"
        type="button"
        class="whitespace-nowrap text-xs text-muted hover:text-primary"
        :title="$t('about.title')"
        @click="open = true"
    >
        v{{ version }}
    </button>

    <!-- A tab left open across an update is yesterday's portal talking to today's API. -->
    <UButton
        v-if="updateAvailable"
        :label="$t('about.update_available')"
        icon="i-lucide-refresh-cw"
        size="xs"
        color="warning"
        variant="subtle"
        @click="reloadNow"
    />

    <UModal
        v-model:open="open"
        :title="$t('about.title')"
        :description="`Rezervacije · v${version}`"
        :ui="{ content: 'mx-4 w-full md:w-3/5 lg:w-1/2 max-w-none' }"
    >
        <template #body>
            <div class="flex flex-col gap-5">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                        <p class="text-xs uppercase text-dimmed">{{ $t('about.version') }}</p>
                        <p class="mt-1 font-medium text-highlighted">v{{ version }}</p>
                    </div>
                    <div>
                        <!-- The commit the running API was built from: two servers can carry
                             the same version and answer differently. -->
                        <p class="text-xs uppercase text-dimmed">{{ $t('about.build') }}</p>
                        <p class="mt-1 font-mono text-sm text-default">{{ build || '—' }}</p>
                    </div>
                </div>

                <USeparator />

                <div class="flex flex-col gap-4">
                    <p class="text-xs uppercase text-dimmed">{{ $t('about.changelog') }}</p>

                    <div v-for="entry in changelog" :key="entry.version" class="flex flex-col gap-1">
                        <div class="flex flex-wrap items-baseline gap-2">
                            <span class="font-semibold text-highlighted">v{{ entry.version }}</span>
                            <span class="text-xs text-muted">{{ formatDay(entry.date) }}</span>
                        </div>

                        <p class="text-sm text-default">{{ entry.summary }}</p>

                        <ul class="ms-4 list-disc text-sm text-muted">
                            <li v-for="(change, index) in entry.changes ?? []" :key="index">{{ change }}</li>
                        </ul>
                    </div>
                </div>
            </div>
        </template>
    </UModal>
</template>

<script lang="ts" setup>
    import { onMounted, onUnmounted, ref } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { formatDay } from '@/libs/dayjs'
    import { PrintSuccess } from '@/libs/toast'
    import { useFetchStore } from '@/stores/fetch'

    /** One released version and what changed in it, as the API keeps it. */
    interface IChangelogEntry {
        version: string
        date:    string
        summary: string
        changes: string[] | null
    }

    const { t }      = useI18n()
    const fetchStore = useFetchStore()

    const open            = ref(false)
    const version         = ref('')
    const build           = ref('')
    const changelog       = ref<IChangelogEntry[]>([])
    const updateAvailable = ref(false)

    /** How often an open tab asks whether a new version was deployed under it. */
    const VERSION_POLL_MS = 5 * 60 * 1000

    const loadVersion = async () => {
        const { response, data } = await fetchStore
            .fetch('/version')
            .get()
            .json<{ version: string, build: string, changelog: IChangelogEntry[] | null }>()

        if (response.value?.status !== 200 || !data.value?.version) return

        // The first answer is what this bundle was served beside; a different later one
        // means the deploy moved under this tab.
        if (version.value && data.value.version !== version.value) {
            if (!updateAvailable.value) PrintSuccess(t('about.update_toast'))

            updateAvailable.value = true

            return
        }

        version.value   = data.value.version
        build.value     = data.value.build ?? ''
        changelog.value = data.value.changelog ?? []
    }

    const reloadNow = () => window.location.reload()

    let poll = 0

    onMounted(() => {
        void loadVersion()
        poll = window.setInterval(() => void loadVersion(), VERSION_POLL_MS)
        window.addEventListener('focus', loadVersion)
    })

    onUnmounted(() => {
        window.clearInterval(poll)
        window.removeEventListener('focus', loadVersion)
    })
</script>
